import { useQuery } from "@apollo/client"
import { ThemeProvider } from "@emotion/react"
import styled from "@emotion/styled"
import { PartnershipTierSelector } from "@pallet/features/ApplylistType/components/PartnershipTierSelector"
import { Box, mq, notify, theme } from "@pallet-hq/hegel"
import mixpanel from "mixpanel-browser"
import { useEffect } from "react"
import { CenteredBox } from "components/atoms"
import Image from "components/Image"
import OnboardingModal from "components/Modals/OnboardingModal"
import useOnboardingModal from "components/Modals/OnboardingModal/hook"
import { MODAL_TYPES, TRACK_EVENTS } from "constants/events"
import { FullWidthPage } from "page-components/atoms/AppPages"
import Footer from "page-components/pallet/NavbarLayout/Footer"
import useAuthContext from "state/useAuthContext"
import { usePalletContext } from "utils/hooks/usePalletContext"
import { usePalletRouter } from "utils/hooks/usePalletRouter"
import { PARTNERSHIP_TIERS_QUERY } from "./gql"
import HireNavbar from "./navbar"
import JobBoardTitleSection from "./title/JobBoard"
import CollectiveTitleSection from "./title/Collective"
import { PartnershipTiersQuery } from "__generated__/PartnershipTiersQuery"
import BookCallBanner from "@pallet/components/BookCallBanner"

const Hire = ({ referrer }: { referrer: "jobs" | "talent" }) => {
    const { pallet } = usePalletContext()
    const router = usePalletRouter()
    const { data, refetch } = useQuery<PartnershipTiersQuery>(
        PARTNERSHIP_TIERS_QUERY,
        {
            variables: { pallet },
        }
    )
    const { authed, loading: authLoading } = useAuthContext()
    const { isOpen, hideModal, routeAfterSubmit } = useOnboardingModal()

    useEffect(() => {
        if (!authed && !authLoading && isOpen) {
            mixpanel.track(TRACK_EVENTS.MODAL_VIEWED, {
                type: MODAL_TYPES.ONBOARDING_AUTH,
                authed,
                referrer,
            })
        }
    }, [isOpen, authed, authLoading, referrer])

    if (authLoading || !data?.applylist?.partnershipTiers?.totalCount) {
        return null
    }

    const TitleSection =
        referrer === "jobs" || !data.applylist.group
            ? JobBoardTitleSection
            : CollectiveTitleSection

    return (
        <ThemeProvider
            theme={{
                ...theme,
                colors: {
                    ...theme.colors,
                    primary:
                        data.applylist.primaryColor ?? theme.colors.primary,
                },
            }}
        >
            <FullWidthPage>
                <HireNavbar step="package" />
                <Wrapper>
                    <CenteredBox
                        flexDirection="column"
                        m="auto"
                        mb="medium"
                        pr={[0, "medium", "medium"]}
                    >
                        {data.applylist.logo?.source && (
                            <CuratorImage
                                colorSlug={pallet || ""}
                                width={72}
                                height={72}
                                src={data.applylist.logo?.source}
                            />
                        )}
                        <TitleSection hasCollective={!!data.applylist.group} />
                    </CenteredBox>
                    <PartnershipTierSelector
                        applylist={data.applylist}
                        recruitingTeam={
                            data.applylist.partnership?.recruitingTeam.uuid
                        }
                        referrer={referrer}
                    />
                    <Box my="large" />
                </Wrapper>
                <Box backgroundColor="gray100" pt="large">
                    <BookCallBanner />
                </Box>
                <Footer applylist={data.applylist} />
            </FullWidthPage>
            <OnboardingModal
                isOpen={isOpen}
                hideModal={hideModal}
                onSuccess={async () => {
                    hideModal()
                    const resp = await refetch()
                    const partnership = resp.data.applylist?.partnership
                    if (partnership && !partnership.canRepurchase) {
                        notify.info(
                            "You have already purchased a partnership with this pallet"
                        )
                        return router.push(
                            `/team/${partnership.recruitingTeam.uuid}/communities`
                        )
                    }
                    if (routeAfterSubmit) await router.push(routeAfterSubmit)
                }}
                subheading="you can post jobs, subscribe to Collectives, track candidates, and so much more."
            />
        </ThemeProvider>
    )
}

export default Hire

const Wrapper = styled.div`
    display: flex;
    flex: 0;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 100vw;
    height: 100%;
    ${mq({
        padding: [theme.space.small, theme.space.medium, theme.space.medium],
    })}
    margin: 0 auto;
    margin-top: ${theme.space.large};
    box-sizing: border-box;
`

const CuratorImage = styled(Image)`
    flex-shrink: 0;
    object-fit: cover;
    margin-bottom: ${theme.space.medium};
    border-radius: ${theme.space.xsmall};
`
